import { gql, useMutation, useQuery } from "@apollo/client";

const LOGIN_MUTATION = gql`
  mutation Login($data: LoginInput!) {
    login(data: $data) { 
      accessToken
      user {
        id
        firstName
        lastName
        email
        phone
        role
      }
    }
  }
`;

const LOGIN_MUTATION_VERCEL = gql`
  mutation LoginVercel($data: LoginInput!) {
    loginVercel(data: $data) { 
      accessToken
      user {
        id
        firstName
        lastName
        email
        phone
        role
      }
    }
  }
`;

export const REGISTER_MUTATION = gql`
  mutation Register($data: RegisterInput!) {
    register(data: $data)
  }
`;

export const VERIFY_EMAIL_MUTATION = gql`
  mutation VerifyEmail($email: String!, $code: String!) {
    verifyEmail(email: $email, code: $code) {
      id
      firstName
      lastName
      userName
      email
      phone
      country
      role
      avatarUrl
      createdAt
      updatedAt
    }
  }
`;


export const ME_QUERY = gql`
  query Me {
    me {
      id
      firstName
      lastName
      userName
      email
      avatarUrl
      country
      phone
      role
      cv
      accountType
      portfolioLink
      linkedinLink
      bio
      language
      isActive
      isVerified
      isAnonymous
      isOnline
      isBlocked
      lastSeen
      agencyType
      createdAt
      updatedAt
      worksLength
      categoryId
      skills {
        id
        name
      }
      
    }
  }
`;


const LOGOUT_MUTATION = gql`
  mutation Logout {
    logout {
      success
      message
    }
  }
`;

const EDIT_PROFILE_MUTATION = gql`
  mutation EditProfile($data: EditProfileInput!) {
    editProfile(data: $data) {
      id
      firstName
      lastName
      userName
      email
      country
      phone
      role
      cv
      accountType
      portfolioLink
      linkedinLink
      bio
      language
      isActive
      isVerified
      isAnonymous
      agencyType
      skills {
        id
        name
      }
      avatarUrl
    }
  }
`;

const UPLOAD_AVATAR_MUTATION = gql`
  mutation UpdateAvatar($input: UpdateAvatar!) {
    updateAvatar(input: $input) {
      id
      firstName
      lastName
      userName
      email
      country
      phone
      role
      cv
      accountType
      portfolioLink
      linkedinLink
      bio
      language
      isActive
      isVerified
      isAnonymous
      agencyType
      avatarUrl
    }
  }
`;

export const GET_ACTIVE_FREELANCERS_QUERY = gql`
  query GetActiveFreelancers($page: Int = 1, $perPage: Int = 25) {
    getActiveFreelancers(page: $page, perPage: $perPage) {
      items {
        id
        firstName
        lastName
        userName
        bio
        categoryId
        avatarUrl
        skills {
          id
          name
        }
        country
        isVerified
        isOnline
        reviewsReceived {
          id
          rating
          comment
          professionalism
          communication
          qualityOfWork
          createdAt
        }
      }
      totalItems
      totalPages
      currentPage
      perPage
    }
  }
`;


export const GET_USER_BY_ID_QUERY = gql`
  query GetUserById($id: ID!) {
    getUserById(id: $id) {
      id
      firstName
      lastName
      isVerified
      isOnline
      lastSeen
      email
      createdAt
      role
      avatarUrl
      country
      bio
    }
  }
`;


const CHANGE_PASSWORD_MUTATION = gql`
  mutation ChangePassword($data: ChangePasswordInput!) {
    changePassword(data: $data)
  }
`;

const FORGOT_PASSWORD_MUTATION = gql`
  mutation ForgotPassword($data: ForgotPasswordInput!) {
    forgotPassword(data: $data)
  }
`;

const RESET_PASSWORD_MUTATION = gql`
  mutation ResetPassword($data: ResetPasswordInput!) {
    resetPassword(data: $data)
  }
`;

export const GET_USER_BY_USERNAME_QUERY = gql`
  query GetUserByUsername($username: String!) {
    getUserByUsername(username: $username) {
      id
      firstName
      lastName
      userName
      email
      phone
      country
      accountType
      language
      role
      agencyType
      bio
      isActive
      isOnline
      isVerified
      isBlocked
      isAnonymous
      portfolioLink
      linkedinLink
      cv
      createdAt
      updatedAt
      avatarUrl
    }
  }
`;

export const useForgotPasswordMutation = () => {
  return useMutation(FORGOT_PASSWORD_MUTATION);
};

export const useResetPasswordMutation = () => {
  return useMutation(RESET_PASSWORD_MUTATION);
};

export const useChangePasswordMutation = () => {
  return useMutation(CHANGE_PASSWORD_MUTATION);
};

export const useLoginMutation = () => {
  return useMutation(LOGIN_MUTATION);
};

export const useLoginVercelMutation = () => {
  return useMutation(LOGIN_MUTATION_VERCEL);
};

export const useRegisterMutation = () => {
  return useMutation(REGISTER_MUTATION);
};

export const useEditProfileMutation = () => {
  return useMutation(EDIT_PROFILE_MUTATION);
};

export const useAuth = () => {
  const { data, loading, error } = useQuery(ME_QUERY, {
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  return {
    user: data?.me ?? null,
    loading,
    error,
    isAuthenticated: !!data?.me,
  };
};

export const useUploadAvatarMutation = () => {
  return useMutation(UPLOAD_AVATAR_MUTATION);
};

export const useLogoutMutation = () => {
  return useMutation(LOGOUT_MUTATION, {
    context: { fetchOptions: { credentials: 'include' } },
  });
};

export const useVerifyEmailMutation = () => {
  return useMutation(VERIFY_EMAIL_MUTATION);
}

export const useGetActiveFreelancers = (page = 1, perPage = 25) => {
  const { data, loading, error, refetch } = useQuery(GET_ACTIVE_FREELANCERS_QUERY, {
    variables: { page, perPage },
    fetchPolicy: "cache-and-network",
  });

  return {
    freelancers: data?.getActiveFreelancers.items ?? [],
    totalItems: data?.getActiveFreelancers.totalItems ?? 0,
    totalPages: data?.getActiveFreelancers.totalPages ?? 0,
    currentPage: data?.getActiveFreelancers.currentPage ?? page,
    perPage: data?.getActiveFreelancers.perPage ?? perPage,
    loading,
    error,
    refetch
  };
};


export const useGetUserById = (id: string) => {
  const { data, loading, error } = useQuery(GET_USER_BY_ID_QUERY, {
    variables: { id },
    fetchPolicy: "network-only", // optional: always refetch
  });

  return {
    user: data?.getUserById ?? null,
    loading,
    error,
  };
};


export const UPLOAD_AVATAR_BASE64_MUTATION = gql`
  mutation UploadAvatar($file: UploadFileInput!) {
    uploadAvatar(file: $file)
  }
`;

export const useUploadAvatarBase64Mutation = () => {
  return useMutation(UPLOAD_AVATAR_BASE64_MUTATION);
};

export const GET_FREELANCERS_STATS_QUERY = gql`
  query GetFreelancersStats {
    getFreelancersStats {
      totalActiveFreelancers
      topCategories {
        categoryId
        name
        totalFreelancers
      }
    }
  }
`;

export const useFreelancersStats = () => {
  const { data, loading, error, refetch } = useQuery(
    GET_FREELANCERS_STATS_QUERY,
    {
      fetchPolicy: "cache-and-network",
    }
  );

  return {
    totalActiveFreelancers:
      data?.getFreelancersStats?.totalActiveFreelancers ?? 0,

    topCategories:
      data?.getFreelancersStats?.topCategories ?? [],

    loading,
    error,
    refetch,
  };
};

export const useUserByUsernameQuery = (username: string) => {
  return useQuery(GET_USER_BY_USERNAME_QUERY, {
    variables: {
      username: decodeURIComponent(username),
    },
    skip: !username,
  });
};